import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiStatus } from "../../utils/apiStatus.enum";
import { UserState } from "../models/userModel";
import httpClient from "../../utils/httpClient";
import { callMsGraphApis } from "../../utils/msalApiCalls";

import { RootState } from "../index";

export interface ResponseError {
  message: string;
}

export const getUserProfile = createAsyncThunk<
  any,
  void,
  { rejectValue: ResponseError }
>("UserProfileSlice/getUserProfile", async (_, thunkApi) => {
  try {
    const userData = await callMsGraphApis("", false);
    return userData;
  } catch (error) {
    console.log("🚀 ~ error:", error);
    return thunkApi.rejectWithValue({ message: "Failed to get user profile" });
  }
});

export const getFrontendPermissions = createAsyncThunk<
  any,
  void,
  { rejectValue: ResponseError }
>("UserProfileSlice/getFrontendPermissions", async (_, thunkApi) => {
  try {
    const response = await httpClient.get("/user");
    if (response.status != 200 || response.data.Status !== "success") {
      if (response.data.Status == "error") {
        return thunkApi.rejectWithValue({
          message: response.data.Message,
        });
      } else {
        return thunkApi.rejectWithValue({
          message: "Failed to get user permissions.",
        });
      }
    }
    if (response.data) {
      return response.data;
    }
    return thunkApi.rejectWithValue({
      message: "Failed to get user permissions.",
    });
  } catch (error) {
    console.log("🚀 ~ error:", error);
    return thunkApi.rejectWithValue({
      message: "Failed to get user permissions.",
    });
  }
});

export const getUserAvatar = createAsyncThunk<
  any,
  void,
  { rejectValue: ResponseError }
>("UserProfileSlice/getUserAvatar", async (_, thunkApi) => {
  try {
    const photoSize = "64x64";

    const avatar: any = await callMsGraphApis(
      `/photos/${photoSize}/$value`,
      true
    );
    if (avatar) {
      const url = window.URL || window.webkitURL;
      const blobUrl = await url.createObjectURL(avatar);
      return blobUrl;
    }
    return thunkApi.rejectWithValue({ message: "Failed to get user avatar" });
  } catch (error) {
    console.log("🚀 ~ error:", error);
    return thunkApi.rejectWithValue({ message: "Failed to get user avatar" });
  }
});

const initialState: UserState = {
  name: "",
  emailId: "",
  id: "",
  avatar: "",
  profileApiStatus: ApiStatus.NONE,
  profileApiError: "",
  userInfo: {},
  userPermissions: [],
  userPermissionsError: "",
  userPermissionsStatus: ApiStatus.NONE,
  userEntityData: []
};

const UserProfileSlice = createSlice({
  name: "UserProfileSlice",
  initialState,
  reducers: {
    resetUseProfile: (state) => {
      state.name = "";
      state.emailId = "";
      state.id = "";
      state.avatar = "";
      state.profileApiStatus = ApiStatus.NONE;
      state.profileApiError = "";
      state.userInfo = {};
      state.userPermissions = [];
      state.userPermissionsError = "";
      state.userPermissionsStatus = ApiStatus.NONE;
    },
  },
  extraReducers(builder) {
    builder.addCase(getUserProfile.pending, (state) => {
      state.profileApiStatus = ApiStatus.LOADING;
    });
    builder.addCase(getUserProfile.rejected, (state, { payload }) => {
      state.profileApiError = payload?.message.toString() || "";
      state.profileApiStatus = ApiStatus.ERROR;
    });
    builder.addCase(getUserProfile.fulfilled, (state, { payload }) => {
      state.profileApiStatus = ApiStatus.SUCCESS;
      state.name = payload?.displayName;
      state.emailId = payload?.mail;
      state.id = payload?.id;
      state.userInfo = {
        firstName: payload?.givenName,
        lastName: payload?.surname,
        emailAddress: payload?.mail,
        userId: payload?.id,
      };
    });
    builder.addCase(getUserAvatar.pending, (state) => {
      state.profileApiStatus = ApiStatus.LOADING;
    });
    builder.addCase(getUserAvatar.rejected, (state, { payload }) => {
      state.profileApiError = payload?.message.toString() || "";
      state.profileApiStatus = ApiStatus.ERROR;
    });
    builder.addCase(getUserAvatar.fulfilled, (state, { payload }) => {
      state.profileApiStatus = ApiStatus.SUCCESS;
      state.avatar = payload;
    });
    builder.addCase(getFrontendPermissions.pending, (state) => {
      state.userPermissionsStatus = ApiStatus.LOADING;
    });
    builder.addCase(getFrontendPermissions.rejected, (state, { payload }) => {
      state.userPermissionsStatus = ApiStatus.ERROR;
      state.userPermissionsError = payload?.message.toString() || "";
    });
    builder.addCase(getFrontendPermissions.fulfilled, (state, { payload }) => {
      if (payload.Status == "error") {
        state.userPermissionsStatus = ApiStatus.ERROR;
        state.userPermissionsError = payload.Message;
      } else if (payload.Status == "success") {
        state.userPermissionsStatus = ApiStatus.SUCCESS;
        state.userPermissions = payload.Data.Roles;
        state.userEntityData = payload.Data.EntityData;
      }
    });
  },
});

export const selectUserProfile = (state: RootState) => {
  return state.UserProfileSlice;
};
export const { resetUseProfile } = UserProfileSlice.actions;
export default UserProfileSlice.reducer;
